import { agencyIdEquals } from '../incidents';
import type { PortalAgencyInfo } from '../agency/configuration';
import type { GeoPoint, IncidentAgency } from '../realtime';

export type AgencyWithDistance = {
  agencyInfo: PortalAgencyInfo;
  distanceFromIncidentInMeters: number | null;
};

export function calculateDistanceInMeters(from: GeoPoint, to: GeoPoint): number {
  const deg2Rad = (deg: number): number => (deg * Math.PI) / 180.0;
  const rad2Deg = (rad: number): number => (rad / Math.PI) * 180.0;

  if (Math.abs(from.latitude - to.latitude) < 0.0001 && Math.abs(from.longitude - to.longitude) < 0.0001) {
    return 0;
  }

  const theta = from.longitude - to.longitude;
  let dist =
    Math.sin(deg2Rad(from.latitude)) * Math.sin(deg2Rad(to.latitude)) +
    Math.cos(deg2Rad(from.latitude)) * Math.cos(deg2Rad(to.latitude)) * Math.cos(deg2Rad(theta));
  dist = Math.acos(dist);
  dist = rad2Deg(dist);
  dist = dist * 60 * 1.1515;
  dist = dist * 1.609344 * 1000;
  return dist;
}

export function notAlreadyInvolvedAgencies(
  relatedAgencies: readonly PortalAgencyInfo[],
  involvedAgencies: readonly IncidentAgency[] | null | undefined,
): PortalAgencyInfo[] {
  return relatedAgencies.filter(
    (agency) => !(involvedAgencies?.some((involvedAgency) => agencyIdEquals(involvedAgency.agencyId, agency.id)) ?? false),
  );
}

export function orderRelatedAgencies(
  notInvolvedAgencies: readonly PortalAgencyInfo[],
  lastCallerPosition: GeoPoint | null,
): AgencyWithDistance[] {
  const withDistance: AgencyWithDistance[] = notInvolvedAgencies.map((agencyInfo) => ({
    agencyInfo,
    distanceFromIncidentInMeters:
      lastCallerPosition !== null && agencyInfo.position !== null
        ? calculateDistanceInMeters(agencyInfo.position, lastCallerPosition)
        : null,
  }));

  return withDistance.sort((a, b) => {
    const aHasDistance = a.distanceFromIncidentInMeters !== null;
    const bHasDistance = b.distanceFromIncidentInMeters !== null;
    if (aHasDistance !== bHasDistance) {
      return aHasDistance ? -1 : 1;
    }
    if (a.distanceFromIncidentInMeters !== null && b.distanceFromIncidentInMeters !== null) {
      return a.distanceFromIncidentInMeters - b.distanceFromIncidentInMeters;
    }
    return 0;
  });
}

function formatN1(value: number, language: string): string {
  return new Intl.NumberFormat(language || 'en', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

export function getAgencyDisplayName(agency: AgencyWithDistance, language: string): string {
  return agency.distanceFromIncidentInMeters === null
    ? agency.agencyInfo.displayName
    : `${agency.agencyInfo.displayName} (${formatN1(agency.distanceFromIncidentInMeters / 1000, language)} km)`;
}
